import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Calculator, TrendingUp, Info, AlertTriangle, Home } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell } from 'recharts';

const STORAGE_KEY = 'home_loan_data';

const HomeLoanCalculator = () => {
  const [propertyValue, setPropertyValue] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).propertyValue : 5000000;
  });
  
  const [downPayment, setDownPayment] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).downPayment : 1000000;
  });
  
  const [interestRate, setInterestRate] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).interestRate : 8.5;
  });
  
  const [loanTenure, setLoanTenure] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).loanTenure : 20;
  });

  const [monthlyRent, setMonthlyRent] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).monthlyRent : 25000;
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      propertyValue,
      downPayment,
      interestRate,
      loanTenure,
      monthlyRent
    }));
  }, [propertyValue, downPayment, interestRate, loanTenure, monthlyRent]);

  const calculateLoan = () => {
    const propValue = parseFloat(propertyValue) || 0;
    const downPmt = parseFloat(downPayment) || 0;
    const rate = parseFloat(interestRate) || 0;
    const tenure = parseFloat(loanTenure) || 0;

    const loanAmount = propValue - downPmt;
    const monthlyRate = rate / 12 / 100;
    const numPayments = tenure * 12;

    let monthlyEMI = 0;
    if (monthlyRate > 0 && numPayments > 0) {
      monthlyEMI = loanAmount * monthlyRate * Math.pow(1 + monthlyRate, numPayments) / (Math.pow(1 + monthlyRate, numPayments) - 1);
    }

    const totalAmount = monthlyEMI * numPayments;
    const totalInterest = totalAmount - loanAmount;
    const ltvRatio = propValue > 0 ? (loanAmount / propValue) * 100 : 0;

    return {
      loanAmount,
      monthlyEMI,
      totalInterest,
      totalAmount,
      ltvRatio,
      downPaymentPercent: propValue > 0 ? (downPmt / propValue) * 100 : 0
    };
  };

  const generateAmortizationData = () => {
    const propValue = parseFloat(propertyValue) || 0;
    const downPmt = parseFloat(downPayment) || 0;
    const rate = parseFloat(interestRate) || 0;
    const tenure = parseFloat(loanTenure) || 0;

    const loanAmount = propValue - downPmt;
    const monthlyRate = rate / 12 / 100;
    const numPayments = tenure * 12;

    let monthlyEMI = 0;
    if (monthlyRate > 0 && numPayments > 0) {
      monthlyEMI = loanAmount * monthlyRate * Math.pow(1 + monthlyRate, numPayments) / (Math.pow(1 + monthlyRate, numPayments) - 1);
    }

    let balance = loanAmount;
    const data = [];
    
    for (let year = 0; year <= tenure; year++) {
      const month = year * 12;
      let yearlyPrincipal = 0;
      let yearlyInterest = 0;

      for (let i = 0; i < 12 && (year * 12 + i) < numPayments; i++) {
        const interestPayment = balance * monthlyRate;
        const principalPayment = monthlyEMI - interestPayment;
        
        yearlyInterest += interestPayment;
        yearlyPrincipal += principalPayment;
        balance -= principalPayment;
      }

      data.push({
        year: year,
        principal: Math.round(yearlyPrincipal),
        interest: Math.round(yearlyInterest),
        balance: Math.round(Math.max(0, balance))
      });
    }

    return data;
  };

  const generateTenureComparison = () => {
    const propValue = parseFloat(propertyValue) || 0;
    const downPmt = parseFloat(downPayment) || 0;
    const rate = parseFloat(interestRate) || 0;

    const loanAmount = propValue - downPmt;
    const monthlyRate = rate / 12 / 100;

    const tenures = [5, 10, 15, 20, 25, 30];
    
    return tenures.map(tenure => {
      const numPayments = tenure * 12;
      let monthlyEMI = 0;
      
      if (monthlyRate > 0 && numPayments > 0) {
        monthlyEMI = loanAmount * monthlyRate * Math.pow(1 + monthlyRate, numPayments) / (Math.pow(1 + monthlyRate, numPayments) - 1);
      }

      const totalAmount = monthlyEMI * numPayments;
      const totalInterest = totalAmount - loanAmount;

      return {
        tenure: `${tenure}Y`,
        emi: Math.round(monthlyEMI),
        totalInterest: Math.round(totalInterest)
      };
    });
  };

  const generateRentVsBuyData = () => {
    const results = calculateLoan();
    const rent = parseFloat(monthlyRent) || 0;
    const years = [1, 3, 5, 10, 15, 20];
    
    return years.map(year => {
      const totalRent = rent * 12 * year * 1.05; // 5% annual rent increase
      const emiPaid = results.monthlyEMI * 12 * year;
      
      return {
        year: `${year}Y`,
        rent: Math.round(totalRent),
        emiPaid: Math.round(emiPaid)
      };
    });
  };

  const getPieChartData = () => {
    const results = calculateLoan();
    return [
      { name: 'Principal', value: Math.round(results.loanAmount) },
      { name: 'Interest', value: Math.round(results.totalInterest) }
    ];
  };

  const results = calculateLoan();
  const amortizationData = generateAmortizationData();
  const tenureComparisonData = generateTenureComparison();
  const rentVsBuyData = generateRentVsBuyData();
  const pieData = getPieChartData();

  const COLORS = ['hsl(var(--primary))', 'hsl(var(--secondary))', 'hsl(var(--accent))', 'hsl(var(--destructive))'];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-gradient-hero">
      <nav className="border-b border-border/40 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </Link>
              <Separator orientation="vertical" className="h-6" />
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg gradient-primary flex items-center justify-center">
                  <Home className="w-5 h-5 text-white" />
                </div>
                <span className="font-display text-lg font-semibold text-foreground">
                  Home Loan Calculator
                </span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="calculator" className="space-y-6">
          <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
            <TabsList className="inline-flex w-auto min-w-full sm:min-w-0 bg-muted/50 p-1 h-auto">
              <TabsTrigger 
                value="calculator" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Calculator className="w-4 h-4 mr-2" />
                Calculator & Results
              </TabsTrigger>
              <TabsTrigger 
                value="charts" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Visual Analysis
              </TabsTrigger>
              <TabsTrigger 
                value="insights" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Info className="w-4 h-4 mr-2" />
                Insights & Explanation
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="calculator" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">What is a Home Loan Calculator?</CardTitle>
                <CardDescription className="text-base leading-relaxed">
                  A home loan calculator helps you estimate your monthly EMI (Equated Monthly Installment), total interest payable, 
                  and other important metrics for your home loan. It helps you plan your finances and make informed decisions about 
                  property purchases.
                </CardDescription>
              </CardHeader>
            </Card>

            <div className="grid lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-1 border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Loan Parameters</CardTitle>
                  <CardDescription>Enter your loan details</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="propertyValue" className="text-sm font-medium">
                      Property Value ($)
                    </Label>
                    <Input
                      id="propertyValue"
                      type="number"
                      value={propertyValue}
                      onChange={(e) => setPropertyValue(e.target.value)}
                      placeholder="5000000"
                      className="text-base"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="downPayment" className="text-sm font-medium">
                      Down Payment ($)
                    </Label>
                    <Input
                      id="downPayment"
                      type="number"
                      value={downPayment}
                      onChange={(e) => setDownPayment(e.target.value)}
                      placeholder="1000000"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">
                      {results.downPaymentPercent.toFixed(1)}% of property value
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="interestRate" className="text-sm font-medium">
                      Interest Rate (% per annum)
                    </Label>
                    <Input
                      id="interestRate"
                      type="number"
                      value={interestRate}
                      onChange={(e) => setInterestRate(e.target.value)}
                      placeholder="8.5"
                      step="0.1"
                      className="text-base"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="loanTenure" className="text-sm font-medium">
                      Loan Tenure (Years)
                    </Label>
                    <Input
                      id="loanTenure"
                      type="number"
                      value={loanTenure}
                      onChange={(e) => setLoanTenure(e.target.value)}
                      placeholder="20"
                      className="text-base"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="monthlyRent" className="text-sm font-medium">
                      Current Monthly Rent ($)
                    </Label>
                    <Input
                      id="monthlyRent"
                      type="number"
                      value={monthlyRent}
                      onChange={(e) => setMonthlyRent(e.target.value)}
                      placeholder="25000"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">For rent vs buy comparison</p>
                  </div>

                  <Button 
                    className="w-full gradient-primary hover:opacity-90 transition-opacity shadow-secondary"
                    onClick={() => setPropertyValue(propertyValue)}
                  >
                    <Calculator className="w-4 h-4 mr-2" />
                    Calculate
                  </Button>
                </CardContent>
              </Card>

              <div className="lg:col-span-2 space-y-6">
                <Card className="border-border/50 shadow-md">
                  <CardHeader>
                    <CardTitle className="font-display">Loan Summary</CardTitle>
                    <CardDescription>Your home loan breakdown</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="p-6 rounded-xl bg-gradient-to-br from-primary-light to-card border border-primary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                            <Calculator className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Loan Amount</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-primary">
                          {formatCurrency(results.loanAmount)}
                        </p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-secondary-light to-card border border-secondary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Monthly EMI</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-secondary">
                          {formatCurrency(results.monthlyEMI)}
                        </p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-accent-light to-card border border-accent/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center">
                            <Calculator className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Total Interest</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-accent">
                          {formatCurrency(results.totalInterest)}
                        </p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-purple-light to-card border border-purple/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-purple flex items-center justify-center">
                            <Home className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">LTV Ratio</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-purple">
                          {results.ltvRatio.toFixed(1)}%
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">Loan-to-Value</p>
                      </div>
                    </div>

                    <Separator className="my-6" />

                    <div className="p-6 rounded-xl bg-gradient-chart">
                      <div className="grid sm:grid-cols-2 gap-6">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-2">Total Amount Payable</p>
                          <p className="text-2xl font-display font-bold text-foreground mb-1">
                            {formatCurrency(results.totalAmount)}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Over {loanTenure} years
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-2">EMI vs Rent</p>
                          <p className="text-2xl font-display font-bold text-foreground mb-1">
                            {results.monthlyEMI > parseFloat(monthlyRent) ? 
                              `$${Math.round(results.monthlyEMI - parseFloat(monthlyRent)).toLocaleString('en-IN')} more` : 
                              `$${Math.round(parseFloat(monthlyRent) - results.monthlyEMI).toLocaleString('en-IN')} less`}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {results.monthlyEMI > parseFloat(monthlyRent) ? 'than current rent' : 'than current rent'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="charts" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Loan Amortization Schedule</CardTitle>
                <CardDescription className="text-base">
                  Year-by-year breakdown of principal and interest payments
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={amortizationData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="year" 
                        label={{ value: 'Year', position: 'insideBottom', offset: -5 }}
                        stroke="hsl(var(--muted-foreground))"
                      />
                      <YAxis 
                        label={{ value: 'Amount ($)', angle: -90, position: 'insideLeft' }}
                        stroke="hsl(var(--muted-foreground))"
                        tickFormatter={(value) => `$${(value / 100000).toFixed(0)}L`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Legend />
                      <Area 
                        type="monotone" 
                        dataKey="principal" 
                        stackId="1"
                        stroke="hsl(var(--primary))" 
                        fill="hsl(var(--primary))"
                        name="Principal"
                      />
                      <Area 
                        type="monotone" 
                        dataKey="interest" 
                        stackId="1"
                        stroke="hsl(var(--destructive))" 
                        fill="hsl(var(--destructive))"
                        name="Interest"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Principal vs Interest Breakdown</CardTitle>
                  <CardDescription>
                    Total payment distribution
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">EMI vs Tenure Comparison</CardTitle>
                  <CardDescription>
                    How loan tenure affects EMI and total interest
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={tenureComparisonData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="tenure" 
                          stroke="hsl(var(--muted-foreground))"
                        />
                        <YAxis 
                          stroke="hsl(var(--muted-foreground))"
                          tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Legend />
                        <Bar dataKey="emi" fill="hsl(var(--primary))" name="Monthly EMI" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Rent vs Buy Comparison</CardTitle>
                <CardDescription className="text-base">
                  Long-term cost analysis of renting vs buying
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={rentVsBuyData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="year" 
                        stroke="hsl(var(--muted-foreground))"
                      />
                      <YAxis 
                        stroke="hsl(var(--muted-foreground))"
                        tickFormatter={(value) => `$${(value / 100000).toFixed(0)}L`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Legend />
                      <Bar dataKey="rent" fill="hsl(var(--destructive))" name="Cumulative Rent" />
                      <Bar dataKey="emiPaid" fill="hsl(var(--secondary))" name="Cumulative EMI" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="insights" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Understanding Your Home Loan</CardTitle>
                <CardDescription className="text-base">
                  Comprehensive guide to home loan calculations and decision-making
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-primary">
                    Loan-to-Value (LTV) Ratio Analysis
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-3">
                    Your LTV ratio is <strong className="text-primary">{results.ltvRatio.toFixed(1)}%</strong>, which means the bank is financing 
                    {' '}<strong className="text-primary">{results.ltvRatio.toFixed(1)}%</strong> of the property value. 
                    Most banks in India allow up to 80-90% LTV for home loans.
                  </p>
                  <div className={`p-6 rounded-xl bg-gradient-to-br ${results.ltvRatio <= 80 ? 'from-secondary-light to-card border-secondary/20' : results.ltvRatio <= 90 ? 'from-accent-light to-card border-accent/20' : 'from-destructive/10 to-card border-destructive/20'} border`}>
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-medium">LTV Assessment:</span>
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        results.ltvRatio <= 80 ? 'bg-secondary/20 text-secondary' :
                        results.ltvRatio <= 90 ? 'bg-accent/20 text-accent' :
                        'bg-destructive/20 text-destructive'
                      }`}>
                        {results.ltvRatio <= 80 ? 'Excellent' : results.ltvRatio <= 90 ? 'Good' : 'High Risk'}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {results.ltvRatio <= 80 ? 
                        'Lower LTV ratio typically means better interest rates and easier loan approval.' :
                       results.ltvRatio <= 90 ?
                        'Your LTV is within acceptable limits. Consider increasing down payment for better rates.' :
                        'High LTV ratio may result in higher interest rates or require mortgage insurance.'}
                    </p>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-secondary">
                    Monthly EMI Breakdown
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-3">
                    Your monthly EMI is <strong className="text-secondary">{formatCurrency(results.monthlyEMI)}</strong>. 
                    This includes both principal and interest components that vary over time.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg bg-primary-light border border-primary/20">
                      <p className="text-sm font-medium text-muted-foreground mb-1">Total Interest</p>
                      <p className="text-2xl font-bold text-primary mb-2">
                        {formatCurrency(results.totalInterest)}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {((results.totalInterest / results.loanAmount) * 100).toFixed(1)}% of loan amount
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary-light border border-secondary/20">
                      <p className="text-sm font-medium text-muted-foreground mb-1">Total Payable</p>
                      <p className="text-2xl font-bold text-secondary mb-2">
                        {formatCurrency(results.totalAmount)}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Over {loanTenure} years
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-accent">
                    Affordability Analysis
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-4">
                    Financial experts recommend that your EMI should not exceed 40-50% of your monthly income. 
                    This ensures you have enough funds for other expenses and savings.
                  </p>
                  <div className="p-4 rounded-lg bg-muted/50 text-sm space-y-2">
                    <p className="font-mono">
                      <strong>Recommended Monthly Income:</strong> {formatCurrency(results.monthlyEMI * 2.5)}+
                    </p>
                    <p className="font-mono">
                      <strong>Based on 40% EMI-to-Income ratio</strong>
                    </p>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-destructive">
                    Important Considerations
                  </h3>
                  <div className="space-y-3 text-base text-muted-foreground leading-relaxed">
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Processing Fees & Charges:</strong> Banks charge 0.5-1% of loan amount as processing fee, 
                        plus other charges like valuation, legal, and technical fees.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Prepayment Options:</strong> Check if your loan allows partial prepayments without penalty. 
                        This can significantly reduce interest burden.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Property Insurance:</strong> Home insurance is often mandatory and costs 0.15-0.50% 
                        of property value annually.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Tax Benefits:</strong> Home loan offers tax deductions - up to $2 lakh on interest (Section 24) 
                        and $1.5 lakh on principal (Section 80C).
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-purple">
                    Rent vs Buy Decision
                  </h3>
                  <div className="space-y-4 text-base">
                    <div className="p-4 rounded-lg bg-secondary-light border-l-4 border-secondary">
                      <h4 className="font-semibold text-foreground mb-2">✅ Consider Buying If:</h4>
                      <ul className="text-muted-foreground space-y-1 list-disc list-inside">
                        <li>You plan to stay in the same location for 7+ years</li>
                        <li>Property prices are expected to appreciate</li>
                        <li>You can afford 20%+ down payment comfortably</li>
                        <li>EMI is comparable to or less than rent</li>
                      </ul>
                    </div>
                    <div className="p-4 rounded-lg bg-accent-light border-l-4 border-accent">
                      <h4 className="font-semibold text-foreground mb-2">✅ Consider Renting If:</h4>
                      <ul className="text-muted-foreground space-y-1 list-disc list-inside">
                        <li>Your job requires frequent relocations</li>
                        <li>You prefer liquidity and investment flexibility</li>
                        <li>Maintenance and repair costs concern you</li>
                        <li>Property prices are stagnant or declining</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-primary">
                    Strategies to Reduce Interest Burden
                  </h3>
                  <div className="space-y-4 text-base">
                    <div className="p-4 rounded-lg bg-primary-light border-l-4 border-primary">
                      <h4 className="font-semibold text-foreground mb-2">💡 Increase Down Payment</h4>
                      <p className="text-muted-foreground">
                        Every $1 lakh increase in down payment reduces your EMI and saves significant interest over the loan tenure.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary-light border-l-4 border-secondary">
                      <h4 className="font-semibold text-foreground mb-2">💡 Make Prepayments</h4>
                      <p className="text-muted-foreground">
                        Annual prepayments of 5-10% of outstanding principal can reduce tenure by several years.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-accent-light border-l-4 border-accent">
                      <h4 className="font-semibold text-foreground mb-2">💡 Choose Shorter Tenure</h4>
                      <p className="text-muted-foreground">
                        Opt for shortest tenure you can afford - saves massive interest despite higher EMI.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-purple-light border-l-4 border-purple">
                      <h4 className="font-semibold text-foreground mb-2">💡 Negotiate Interest Rate</h4>
                      <p className="text-muted-foreground">
                        Compare rates across banks. Even 0.25% reduction can save lakhs over loan tenure.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default HomeLoanCalculator;
